import { QUESTION_TYPE } from '../../../../constants/questionTypes';
import type { SliderMatrixQuestion } from '../../../../types/index';
import type { ApiQuestion } from '../../../../internalTypes';
import type { ResolvedQuestionShell } from '../shared/resolveQuestionShell';
import { buildSliderMatrixRows } from './buildSliderMatrixRows';

export function mapSliderMatrixQuestion(
  raw: ApiQuestion,
  shell: ResolvedQuestionShell,
  language?: string
): SliderMatrixQuestion {
  const configs = raw.questionConfigs || [];
  const sliderConfig = configs.find(c => c.key === 'SLIDER_TICKS_AND_VALUES');
  const childConfigs = sliderConfig?.childConfigs || [];

  const rawMin = childConfigs.find(c => c.key === 'TICK_START')?.value?.[0];
  const rawMax = childConfigs.find(c => c.key === 'TICK_END')?.value?.[0];
  const rawStep = childConfigs.find(c => c.key === 'STEP_SIZE')?.value?.[0];
  const rawTicks = sliderConfig?.value?.[0];

  const min = rawMin !== undefined ? Number(rawMin) : 0;
  const max = rawMax !== undefined ? Number(rawMax) : 10;
  const step = rawStep !== undefined ? Number(rawStep) : 1;
  const tickCount = rawTicks !== undefined ? Number(rawTicks) : 10;

  const enrichedRaw = {
    ...raw,
    additional: {
      ...raw.additional,
      sliderMin: min,
      sliderMax: max,
      sliderStep: step,
    },
  };

  const statementRows = buildSliderMatrixRows(enrichedRaw, language);

  const scaleAnchorLabels = (raw.answerDetails || [])
    .filter(a => a.additional?.isHintText)
    .map(a => (language && a.translations?.[language]?.answerText) || a.answerText || '')
    .filter(Boolean);

  const getFlag = (keys: string[]) => {
    for (const key of keys) {
      const c = configs.find(x => x.key === key) || childConfigs.find(x => x.key === key);
      if (c?.value?.length) {
        return c.value[0]?.toString().toLowerCase() === 'true';
      }
    }
    return false;
  };

  const isGraphics = configs
    .find(c => c.key === 'SCALE_TYPE' || c.key === 'VIEW_VARIANT')
    ?.value?.[0]
    ?.toUpperCase()
    .includes('GRAPHIC');

  const enableInputBox = getFlag(['ENABLE_INPUT_BOX', 'SHOW_INPUT_BOX', 'INPUT_BOX', 'ENABLE_INPUT']);
  const displayValues = getFlag(['SHOW_VALUE', 'DISPLAY_VALUES']);
  const hasNotApplicableOption = getFlag(['NOT_APPLICABLE', 'NOT_APPLICABLE_OPTIONS']);

  const answerOptions = (raw.answerDetails || [])
    .filter(a => !a.additional?.isHintText && !a.hidden)
    .map(a => a.translations?.[language || '']?.answerText || a.answerText || '');

  const marks: SliderMatrixQuestion['ticks']['marks'] = [];
  if (tickCount > 1) {
    const markStep = (max - min) / (tickCount - 1);
    for (let i = 0; i < tickCount; i++) {
      const tickPosition = Number((min + i * markStep).toFixed(2));
      const tickLabel = answerOptions[i] || tickPosition.toString();
      marks.push({ tickPosition, tickLabel });
    }
  }

  return {
    ...shell,
    type: QUESTION_TYPE.SLIDER_MATRIX,
    statementRows,
    ticks: {
      count: tickCount,
      marks,
    },
    enableInputBox,
    displayValues,
    hasNotApplicableOption,
    sliderStyle: isGraphics ? 'graphics' : 'standard',
    ...(scaleAnchorLabels.length > 0 ? { scaleAnchorLabels } : {}),
  };
}
